/**
 * PROP FIELD — the dressed environment for HOME FIELD.
 * Spec: bible 03-STAGES §1.1.
 *
 * Places the forged prop kit along the scroll as an authored composition rather
 * than scattered noise. Three ideas drive the layout:
 *
 *   1. CLUSTERS, NOT CONFETTI. A barn alone is a box. A barn with a silo, a
 *      grain bin, a parked tractor and a fence run is a FARM. The eye reads
 *      relationships, so props are placed in groups that imply human use.
 *   2. THE SCROLL IS A JOURNEY. Layout is a function of distance travelled:
 *      open pasture, then working farms, then the river crossing, then the
 *      Guard installation. The stage should feel like flying somewhere.
 *   3. MOTION IS THE CHEAPEST BEAUTY. Windmill rotors turn, the herd drifts,
 *      orchard canopies sway. A static field is scenery; a moving one is a place.
 *
 * Everything is GPU-instanced per prop type — one draw call per kind regardless
 * of count — and none of it collides. Terrain cannot kill the player.
 */

import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { textureForMaterial } from '../assets/propTextures'
import { corridors } from './terrainShape'

/** One placed instance. */
interface Placement {
  x: number
  y: number
  rot: number
  scale: number
  /** Per-instance animation phase so a herd does not move in lockstep. */
  phase: number
  /** Remaining structure. 0 = destroyed and hidden. */
  hp: number
  /** Seconds since destruction, drives the collapse animation. */
  wreckTime: number
}

/**
 * Destructible props. HP is deliberately low — these are scenery, not enemies,
 * and blowing up a silo should feel like a bonus, never like a health sponge.
 * `radius` is the damage footprint; `score` is what the player banks for it.
 *
 * Props with no entry here are indestructible (terrain, craters, fences).
 */
const DESTRUCTIBLE: Record<string, { hp: number; radius: number; score: number; tier: 'light' | 'armored' | 'structure' }> = {
  'prop-barn':          { hp: 220, radius: 4.2, score: 800,  tier: 'armored' },
  'prop-farmhouse':     { hp: 180, radius: 3.4, score: 600,  tier: 'armored' },
  'prop-silo':          { hp: 260, radius: 2.2, score: 1200, tier: 'structure' },
  'prop-grain-bin':     { hp: 200, radius: 2.8, score: 900,  tier: 'armored' },
  'prop-water-tower':   { hp: 240, radius: 2.4, score: 1500, tier: 'structure' },
  'prop-windmill':      { hp: 160, radius: 1.8, score: 2000, tier: 'structure' },
  'prop-machine-shed':  { hp: 150, radius: 4.0, score: 500,  tier: 'armored' },
  'prop-hay-bale':      { hp: 40,  radius: 1.0, score: 150,  tier: 'light' },
  'prop-tractor':       { hp: 120, radius: 1.6, score: 700,  tier: 'armored' },
  'prop-pickup-truck':  { hp: 100, radius: 2.2, score: 600,  tier: 'armored' },
  'prop-telegraph-pole':{ hp: 60,  radius: 0.9, score: 250,  tier: 'light' },
  'prop-power-pylon':   { hp: 300, radius: 2.0, score: 1800, tier: 'structure' },
  'prop-orchard-tree':  { hp: 70,  radius: 1.3, score: 200,  tier: 'light' },
  // Guard installation — the fuel tank is the big one. It should cook off.
  'prop-fuel-tank':     { hp: 140, radius: 3.0, score: 2500, tier: 'structure' },
  'prop-hangar':        { hp: 400, radius: 5.5, score: 3000, tier: 'structure' },
  'prop-revetment':     { hp: 350, radius: 3.6, score: 900,  tier: 'structure' },
}

export interface PropHit {
  x: number
  y: number
  destroyed: boolean
  score: number
  tier: 'light' | 'armored' | 'structure'
}

interface PropType {
  id: string
  /** Merged geometry per material, so each prop is a handful of draws. */
  batches: { mesh: THREE.InstancedMesh; placements: Placement[] }[]
  placements: Placement[]
  /** Rotation about the vertical, radians/sec. */
  spin?: { speed: number }
  /** Rotation about the axis the prop faces, radians/sec (windmill fan). */
  spinFacing?: number
  /** Offset onto whatever this prop rides on top of, in playfield units. */
  lift?: { y: number; z: number }
  /** Gentle sway, for foliage. */
  sway?: number
  /** Slow wander, for livestock. */
  graze?: number
}

/** Total scroll length of one layout cycle, in world units. */
const CYCLE = 900

/**
 * One authored entry: `[x, y]`, optionally `[x, y, rotation, scale]`.
 * Distinct from `Placement`, which is the resolved runtime instance.
 */
type LayoutEntry = [number, number, number?, number?]

/**
 * Placement pitch for the linear carriers. CONTRACT with
 * `scripts/model-forge/stage1-props.mjs`, which builds each carrier's wire
 * span to exactly this length. Change one and you must change both — a span
 * shorter than the pitch leaves the line hanging in mid-air.
 */
const TELEGRAPH_PITCH = 40
const PYLON_PITCH = 120

/** Evenly spaced placements down one side, closing exactly at the wrap. */
function run(x: number, pitch: number, start: number): LayoutEntry[] {
  const out: LayoutEntry[] = []
  for (let y = start; y < start + CYCLE; y += pitch) out.push([x, y % CYCLE])
  return out
}

/**
 * Windmill sites, shared by the tower and the fan.
 *
 * The fan is a separate prop (it spins on its own axis; see the forge), so the
 * two lists MUST stay identical — a fan placed one unit off its tower hangs in
 * the air. Referencing one constant twice makes that impossible.
 */
const WINDMILL_SITES: LayoutEntry[] = [[-16.5, 26], [16.5, 92], [-16.5, 160]]

/**
 * The authored layout, written ONE ZONE AT A TIME and merged below.
 *
 * Zones are separate objects rather than comment-delimited sections of one
 * object for a reason that cost real work to find: a single object literal
 * lets the same prop key appear in two zones, and the later one silently
 * DELETES the earlier one. A farmhouse listed in both the farms zone and the
 * settlement zone would erase every farm. Merging makes that impossible —
 * a repeated key concatenates, which is always what was meant.
 *
 * Positions are in cycle-space (0..CYCLE) and repeat. Hand-placed, because a
 * farm is a designed thing and random scatter reads as random scatter.
 *
 * DENSITY. The player sees roughly 45 units of scroll at once. A zone carried
 * by five buildings spread over 140 units therefore puts ONE building on
 * screen at a time and reads as scattered farmland regardless of what the
 * zone was meant to be. Populated zones need 6-12 placements per 45 units.
 */
const ZONES: Record<string, LayoutEntry[]>[] = [
  /* ═══ 0-180 — OPEN PASTURE ══════════════════════════════════════════════
   * The quiet opening. Herd, fences, a lone windmill. Almost nothing built.
   * The stage has to start empty so everything after it reads as escalation.
   * The quilt carries this zone now — the ground itself is the content. */
  {
    // ONE ANIMAL IS A TEXTURE, THREE ARE A FARM. The pasture originally ran
    // 32 cows and nothing else, which read as repetition rather than as
    // livestock. Each paddock now holds a single species, because mixed herds
    // read as scatter — real fields are separated by what is grazing in them.
    'prop-cow': [
      [-13, 14], [-11.5, 16.5], [-14.5, 18], [-12, 20.5], [-15, 22],
      [11, 30], [13, 32.5], [10, 34], [12.5, 36],
    ],
    'prop-horse': [
      [-9, 74], [-11.5, 76.5], [-8.5, 79], [-12, 81],
      [13, 150], [15, 152.5], [11.5, 154],
    ],
    'prop-sheep': [
      [12, 116], [13.2, 117.4], [10.9, 118.2], [13.8, 119.6], [11.6, 120.4],
      [12.8, 121.8], [10.4, 122.2], [14.2, 123], [12.2, 124.4], [13.4, 125.6],
    ],
    'prop-fence-run': [
      [-16, 12, 0], [-16, 23, 0], [16, 28, 0], [16, 39, 0],
      [-16, 70, 0], [-16, 81, 0], [16, 114, 0], [16, 125, 0],
      [-16, 146, 0], [-16, 157, 0],
    ],
    'prop-windmill': WINDMILL_SITES,
    'prop-windmill-rotor': WINDMILL_SITES,
    'prop-hay-bale': [
      [-8, 62], [-6.5, 63.5], [-9.5, 64], [-7, 66],
      [9, 100], [11, 101.5], [7.5, 103],
      [-11, 136], [-9, 138], [-12.5, 140],
    ],
    // One working farm at the very end of the pasture, so the transition into
    // the farms zone is a build rather than a cut.
    'prop-barn': [[14, 168]],
    'prop-silo': [[11.5, 166]],
  },

  /* ═══ 180-380 — WORKING FARMS ═══════════════════════════════════════════
   * Red roofs, silos, machinery. The stage's signature image. */
  {
    'prop-barn': [[-13, 190], [14, 268], [-14, 356], [15, 232], [-15.5, 314]],
    'prop-hay-bale': [
      [9, 210], [11, 211.5], [7.5, 213],
      [-10, 248], [-8, 250], [-11.5, 252],
      [12, 330], [10, 332], [13.5, 334],
    ],
    'prop-cow': [
      [14, 340], [12, 342.5], [15.5, 344], [13, 347],
    ],
    'prop-horse': [[-14, 222], [-12.5, 224.5], [-15.5, 226]],
    'prop-sheep': [
      [15.5, 296], [14.4, 297.2], [16.2, 298.4], [14.9, 299.6], [15.9, 300.8],
    ],
    'prop-fence-run': [[16, 338, 0], [16, 349, 0], [-16, 220, 0], [-16, 231, 0]],
    'prop-tobacco-barn': [[15, 204], [-15.5, 300], [14.5, 372], [-14, 244], [16, 326]],
    'prop-farmhouse': [[-16.5, 198], [16.5, 262], [-16, 364], [16.5, 208], [-16, 306]],
    'prop-silo': [
      [-9.5, 188], [-11, 192], [10.5, 270], [-10, 354],
      [12.5, 234], [-12, 316], [12, 272.5],
    ],
    'prop-grain-bin': [[-16, 186], [16, 274], [-15, 360], [-16.5, 240], [16.5, 320]],
    'prop-chicken-house': [[16, 232], [-16.5, 318], [-16.5, 194], [16, 366]],
    'prop-corn-crib': [[-11, 216], [12.5, 292], [-13, 380], [13, 250], [-12, 336]],
    'prop-machine-shed': [[-15, 200], [15, 278], [14, 368], [-14.5, 256], [15.5, 342]],
    'prop-tractor': [[-11, 208], [12, 280], [-12, 358], [13.5, 238], [-13, 322]],
    'prop-pickup-truck': [
      [-15.5, 196], [15.5, 266], [11, 376], [15, 228], [-15, 310], [12, 296],
    ],
  },

  /* ═══ 380-520 — CROSSROADS SETTLEMENT ═══════════════════════════════════
   * A church, a store, a diner. The only place in the stage where people
   * clearly gathered — which is what makes the occupation land.
   *
   * A town has to be CROWDED for the two seconds you are over it. */
  {
    'prop-church': [[-15, 430]],
    'prop-general-store': [[15.5, 444], [-13.5, 462]],
    'prop-diner': [[14, 478]],
    'prop-grain-elevator': [[-16, 496]],
    'prop-water-tower': [[15, 412], [-16, 508]],
    // Houses fronting the road, outbuildings behind them, vehicles parked
    // between — what makes a crossroads read as a place people live.
    'prop-farmhouse': [
      [-11.5, 424], [-11, 438], [11.5, 434], [11, 452],
      [-12, 470], [12, 468], [-11.5, 486], [11.5, 492],
    ],
    'prop-machine-shed': [[-16.5, 418], [16.5, 424], [-16, 480], [16, 502]],
    'prop-pickup-truck': [
      [-13.5, 428], [13.5, 440], [-13, 456], [13, 474], [-13.5, 490], [13, 506],
    ],
    'prop-grain-bin': [[-16.5, 446], [16.5, 488]],
    'prop-corn-crib': [[-16.5, 458], [16.5, 460]],
    'prop-hay-bale': [[-12, 402], [-10, 404], [-13.5, 406]],
  },

  /* ═══ 520-660 — WOODLAND AND THE CROSSING ═══════════════════════════════
   * Orchard belts thicken, then the river crossing. A visual breather, and
   * the only two moments in the stage with a structure spanning the water. */
  {
    'prop-orchard-tree': [
      [-17, 530], [-17, 533], [-17, 536], [-17, 539], [-14.5, 531.5],
      [-14.5, 534.5], [-14.5, 537.5], [-12, 533], [-12, 536.5], [-12, 540],
      [17, 548], [17, 551], [17, 554], [14.5, 549.5], [14.5, 552.5], [12, 551],
      [17, 557], [14.5, 555.5], [12, 554.5],
      [-17, 596], [-17, 599], [-17, 602], [-14.5, 597.5], [-14.5, 600.5],
      [-12, 599], [17, 610], [17, 613], [14.5, 611.5], [12, 613],
      [-17, 630], [-14.5, 632], [17, 636], [14.5, 638], [-12, 634],
    ],
    'prop-covered-bridge': [[0, 578]],
    'prop-girder-bridge': [[0, 648]],
    'prop-sheep': [[-14, 566], [-12.8, 567.2], [-15.2, 568.4], [-13.4, 569.6]],
    'prop-fence-run': [[-16, 564, 0], [-16, 575, 0]],
  },

  /* ═══ 660-900 — GUARD INSTALLATION ══════════════════════════════════════
   * Pastoral gives way to grey concrete. The boss approach.
   *
   * This zone previously claimed 660-900 in its header while placing nothing
   * before 800 — 140 units of empty ground the player crossed expecting the
   * escalation the layout promised. It now actually starts where it says. */
  {
    // The perimeter comes first: fence line, then revetments, then the
    // hardened buildings. Arriving at a base should feel like passing through
    // layers, not like a barn suddenly turning grey.
    'prop-revetment': [
      [-15, 676], [15, 682], [-16, 700], [16, 706],
      [-15, 758], [15, 764], [0, 862], [-15, 878], [15, 820], [-15, 820],
    ],
    'prop-fuel-tank': [
      [-16, 690], [16, 694], [-16, 800], [16, 800], [-16, 852], [16, 870],
      [-16.5, 744], [16.5, 748],
    ],
    'prop-hangar': [
      [-11, 718], [11, 736], [-11, 812], [11, 838], [-11.5, 776], [11.5, 790],
    ],
    'prop-machine-shed': [[-16, 728], [16, 772], [-16, 830], [16, 856]],
    'prop-pickup-truck': [[-13, 712], [13, 742], [-13, 806], [13, 848]],
    'prop-water-tower': [[16.5, 724], [-16.5, 846]],
    'prop-crater': [[-4, 732], [7, 768], [-9, 824], [5, 858]],
  },

  /* ═══ THROUGHOUT ════════════════════════════════════════════════════════ */
  {
    // GENERATED, NOT LISTED. Each carrier's wire span is built to exactly its
    // pitch (see TELEGRAPH_PITCH / PYLON_PITCH in
    // scripts/model-forge/stage1-props.mjs), so the placement spacing and the
    // model must agree or the line breaks in the sky. Deriving the positions
    // from the same constant removes the chance of a hand-edited list drifting.
    // Both pitches divide CYCLE exactly, so the run also closes at the wrap.
    'prop-telegraph-pole': run(-18, TELEGRAPH_PITCH, 20),
    'prop-power-pylon': run(17.5, PYLON_PITCH, 96),
    'prop-crater': [
      [-5, 34], [6, 158], [-8, 236], [9, 314], [-4, 392], [7, 470],
      [-9, 548], [5, 632], [12, 142], [-13, 428], [11, 716], [-6, 794], [8, 866],
    ],
  },
]

/**
 * Merge the zones into the flat map the loader consumes. Repeated keys
 * concatenate — see the note on ZONES for why this is not a plain literal.
 */
const LAYOUT: Record<string, LayoutEntry[]> = (() => {
  const merged: Record<string, LayoutEntry[]> = {}
  for (const zone of ZONES) {
    for (const [id, places] of Object.entries(zone)) {
      ;(merged[id] ??= []).push(...places)
    }
  }
  return merged
})()

/**
 * Props whose orientation carries meaning and must NOT be randomised.
 *
 * The default per-instance rotation exists so repeated barns do not read as
 * clones, but applying it to a telegraph pole spins its 40-unit wire span off
 * to a random bearing — which was half of why the power lines looked
 * disconnected. It is equally wrong on an air base: hangars, revetments and
 * fuel tanks are laid out on a surveyed grid, and randomly-yawed hangars read
 * as debris rather than as an installation.
 */
const FIXED_ROT = new Set([
  'prop-telegraph-pole',
  'prop-power-pylon',
  'prop-fence-run',
  'prop-covered-bridge',
  'prop-girder-bridge',
  'prop-windmill-rotor',
  // Guard installation — built, not scattered.
  'prop-hangar',
  'prop-revetment',
  'prop-fuel-tank',
])

/**
 * Props exempt from terrain clearance, because reaching across the water is
 * the whole point of them.
 *
 * This is a SEPARATE concern from fixed rotation and must stay separate: the
 * two were once one set, which meant a hangar could not be aligned to the base
 * grid without also being allowed to stand in the middle of the James.
 */
const SPANS_TERRAIN = new Set([
  'prop-telegraph-pole',
  'prop-power-pylon',
  'prop-fence-run',
  'prop-covered-bridge',
  'prop-girder-bridge',
])

/**
 * Push a placement clear of the river and the road.
 *
 * WHY THIS EXISTS. The layout is authored in cycle-space (0..900) while the
 * terrain repeats every 220, so a prop at a fixed x meets a different part of
 * the meander on every pass — it can sit in a clear field on one lap and in
 * the middle of the James on the next. Hand-checking every placement against a
 * curve that moves under it is not something a person does reliably, and the
 * evidence that it was not being done was hangars floating in the water.
 *
 * The authored Y is the journey design and is never touched. Only X moves, and
 * only outward on the side it was already on, so a farm authored on the river
 * bank stays on the river bank — it just stops standing in the river.
 */
function clearOfTerrain(x: number, y: number, halfWidth: number, id: string): number {
  // LOCAL corridors, not the whole-stage worst case.
  //
  // This used to clear against the river's full sweep across every y, because
  // the terrain repeated every 220 units while the layout ran on 900 — so a
  // prop met a different part of the meander on each pass and only the worst
  // case was safe. That reserved a 16-unit-wide no-build strip and pushed
  // whole farms to the edge of the playfield.
  //
  // The two periods are now both 900, so a prop at a given cycle-y always
  // meets the SAME piece of river. Clearing against the local corridor gives
  // back most of the map. If the periods are ever decoupled again, this must
  // go back to worstCaseCorridors().
  const bars = [...corridors(y - 4), ...corridors(y), ...corridors(y + 4)]
  let out = x
  for (const [lo, hi] of bars) {
    if (out + halfWidth <= lo || out - halfWidth >= hi) continue
    const toLeft = lo - halfWidth - 0.4
    const toRight = hi + halfWidth + 0.4
    out = Math.abs(out - toLeft) <= Math.abs(out - toRight) ? toLeft : toRight
  }
  if (import.meta.env.DEV && Math.abs(out - x) > 0.01) {
    console.warn(
      `[PropField] ${id} at x=${x.toFixed(1)} y=${y.toFixed(0)} overlaps terrain; moved to ${out.toFixed(1)}`,
    )
  }
  return out
}

/**
 * Clearance half-width overrides, in world units.
 *
 * Props that must resolve to the SAME x as another prop need the same
 * clearance input, and a measured bounding box will not give that — the
 * windmill's box includes its stock tank, the fan's does not, so measuring
 * each would slide the fan off its tower by nearly two units.
 */
const CLEARANCE_WIDTH: Record<string, number> = {
  'prop-windmill': 3.2,
  'prop-windmill-rotor': 3.2,
}

/**
 * Props that keep a loose common heading instead of a fully random one, in
 * radians of total spread.
 *
 * Cattle in a field align with each other — they graze in roughly the same
 * direction rather than pointing every which way. Full 2*PI randomisation on a
 * herd produces a scatter of differently-angled boxes, which is precisely what
 * a parking lot looks like from above. Narrowing the spread is most of what
 * separates "livestock" from "parked vehicles" at this scale.
 */
const ROT_SPREAD: Record<string, number> = {
  'prop-cow': 0.85,
  'prop-horse': 0.7,
  'prop-sheep': 1.1,
}

/**
 * Props that spin about their own facing axis rather than about the vertical.
 *
 * The windmill previously used the vertical spin below, which turned the whole
 * TOWER like a turntable — at this camera angle that read as a jumble of
 * thrashing bars, not as a rotor. The fan is now its own prop and turns about
 * the axis it actually faces.
 */
const SPIN_FACING: Record<string, number> = { 'prop-windmill-rotor': 2.1 }

/**
 * Vertical offset in playfield units, for props that ride on top of another.
 *
 * CONTRACT with the forge: the fan is authored centred on the origin, so this
 * is what puts it back at the head of its tower. The tower is 7.5 u tall with
 * its head at 7.7, and the fan sits 0.72 forward of the mast.
 */
const LIFT: Record<string, { y: number; z: number }> = {
  'prop-windmill-rotor': { y: -0.72, z: 7.7 },
}

const SPIN: Record<string, number> = {}
const SWAY: Record<string, number> = { 'prop-orchard-tree': 0.045 }
const GRAZE: Record<string, number> = {
  'prop-cow': 0.35,
  // Horses drift further and faster; sheep barely move but stay tightly bunched.
  'prop-horse': 0.5,
  'prop-sheep': 0.22,
}

export class PropField {
  readonly group = new THREE.Group()
  private types: PropType[] = []
  private scrolled = 0
  private time = 0
  private readonly dummy = new THREE.Object3D()

  constructor() {
    this.group.name = 'HomeFieldProps'
  }

  async load(): Promise<void> {
    const loader = new GLTFLoader()
    const ids = Object.keys(LAYOUT)

    await Promise.all(
      ids.map(async (id) => {
        try {
          const gltf = await loader.loadAsync(`/models/thunderhead/${id}.glb`)
          const src = gltf.scene
          src.rotation.x = Math.PI / 2 // author-space +Y up -> playfield
          src.updateMatrixWorld(true)

          // The prop's own half-width, measured from the model rather than
          // assumed. Placement clearance depends on how wide the thing
          // actually is, and only the GLB knows that.
          const box = new THREE.Box3().setFromObject(src)
          const halfWidth = Math.max(0.2, (box.max.x - box.min.x) / 2)

          const placements: Placement[] = (LAYOUT[id] ?? []).map(([x, y, rot, scale], i) => ({
            x: SPANS_TERRAIN.has(id) ? x : clearOfTerrain(x, y, CLEARANCE_WIDTH[id] ?? halfWidth, id),
            y,
            // Deterministic per-instance rotation and scale. A hash of the
            // index gives variation that looks random but replays exactly —
            // which the validator's determinism contract requires.
            rot:
              rot ??
              (FIXED_ROT.has(id)
                ? 0
                : ROT_SPREAD[id] !== undefined
                  ? ((i * 2.399963) % 1) * ROT_SPREAD[id] - ROT_SPREAD[id] / 2
                  : (i * 2.399963) % (Math.PI * 2)),
            // Aligned props keep unit scale too: a pole scaled to 0.9 carries a
            // 36-unit wire across a 40-unit gap, which is the same disconnected
            // line by a different route.
            scale:
              scale ??
              (FIXED_ROT.has(id) ? 1 : 0.86 + (((i * 2654435761) % 1000) / 1000) * 0.3),
            phase: (i * 0.618) % 1,
            hp: DESTRUCTIBLE[id]?.hp ?? Infinity,
            wreckTime: -1,
          }))
          if (placements.length === 0) return

          // Merge by material so each prop costs a few instanced draws, not one
          // per part. 70 named parts on a windmill would otherwise be 70 calls.
          const byMaterial = new Map<THREE.Material, THREE.BufferGeometry[]>()
          src.traverse((o) => {
            const m = o as THREE.Mesh
            if (!m.isMesh) return
            const mat = Array.isArray(m.material) ? m.material[0] : m.material
            const g = m.geometry.clone()
            g.applyMatrix4(m.matrixWorld)
            const list = byMaterial.get(mat) ?? []
            list.push(g)
            byMaterial.set(mat, list)
          })

          // Merge every part of this prop into ONE geometry regardless of
          // material, using the dominant material. Props are small, distant,
          // and unlit-flat enough that per-part material fidelity is invisible
          // at gameplay scale — and this takes each prop from ~4 draw calls to 1.
          // ONE BATCH PER MATERIAL, not one batch per prop.
          //
          // This previously merged every part of a prop into a single mesh
          // wearing whichever material had the most parts, on the reasoning
          // that props are "small, distant, and unlit-flat enough that
          // per-part material fidelity is invisible at gameplay scale."
          //
          // That is true of a cow. It is emphatically false of a barn, a
          // hangar, a church or a grain elevator, which are among the largest
          // objects on screen. The result was that a structure authored with a
          // red roof, board walls and galvanised trim rendered as one flat red
          // slab — the "solid red building" report — and its authored
          // materials were simply discarded at load.
          //
          // Splitting by material costs roughly three draw calls per prop
          // instead of one. At ~28 prop types that is well inside budget, and
          // it is the difference between a modelled structure and a coloured
          // box.
          const batches: PropType['batches'] = []
          for (const [material, geos] of byMaterial) {
            const merged = mergeGeometries(geos)
            if (!merged) continue

            let mat = material
            const std = material as THREE.MeshStandardMaterial
            const tex = std.color ? textureForMaterial(material.name, std.color) : null
            if (tex) {
              mat = std.clone()
              const m = mat as THREE.MeshStandardMaterial
              m.map = tex
              // Let the map carry the value; keep the base near white so the
              // texture is not double-darkened by the original colour.
              m.color.setScalar(1)
              m.needsUpdate = true
            }
            const inst = new THREE.InstancedMesh(merged, mat, placements.length)
            inst.name = `${id}_${material.name || 'mat'}`
            inst.frustumCulled = false
            inst.castShadow = true
            inst.receiveShadow = false
            this.group.add(inst)
            batches.push({ mesh: inst, placements })
          }

          this.types.push({
            id,
            batches,
            placements,
            spin: SPIN[id] ? { speed: SPIN[id] } : undefined,
            spinFacing: SPIN_FACING[id],
            lift: LIFT[id],
            sway: SWAY[id],
            graze: GRAZE[id],
          })
        } catch {
          /* a missing prop must never break the stage */
        }
      }),
    )
    this.sync()
  }

  update(dt: number, scrollSpeed: number): void {
    this.scrolled += scrollSpeed * dt
    this.time += dt
    for (const t of this.types) {
      for (const p of t.placements) {
        if (p.wreckTime >= 0) p.wreckTime += dt
      }
    }
    this.sync()
  }

  /**
   * Apply damage in a world-space radius. Returns every prop that was hit so
   * the caller can spawn the right VFX, audio, and score.
   *
   * This is what makes bombs feel like ordnance rather than a screen-clear:
   * dropping one in a farmyard should level the farmyard.
   */
  damageArea(wx: number, wy: number, radius: number, amount: number): PropHit[] {
    const hits: PropHit[] = []
    for (const type of this.types) {
      const spec = DESTRUCTIBLE[type.id]
      if (!spec) continue
      for (const p of type.placements) {
        if (p.hp <= 0) continue
        // Compare against the CURRENT scrolled position, not layout space.
        let y = p.y - (this.scrolled % CYCLE)
        if (y < -34) y += CYCLE
        if (y > CYCLE - 34) y -= CYCLE
        const dx = p.x - wx
        const dy = y - wy
        const reach = radius + spec.radius
        if (dx * dx + dy * dy > reach * reach) continue

        p.hp -= amount
        const destroyed = p.hp <= 0
        if (destroyed) p.wreckTime = 0
        hits.push({ x: p.x, y, destroyed, score: destroyed ? spec.score : 0, tier: spec.tier })
      }
    }
    return hits
  }

  private sync(): void {
    for (const type of this.types) {
      for (const batch of type.batches) {
        for (let i = 0; i < type.placements.length; i++) {
          const p = type.placements[i]
          // Wrap through the cycle so the layout repeats seamlessly.
          let y = p.y - (this.scrolled % CYCLE)
          if (y < -34) y += CYCLE
          if (y > CYCLE - 34) y -= CYCLE

          let x = p.x
          let rot = p.rot

          // Livestock drift slowly across their paddock, out of phase.
          if (type.graze) {
            x += Math.sin(this.time * type.graze + p.phase * 6.28) * 1.4
            rot = p.rot + Math.sin(this.time * type.graze * 0.6 + p.phase * 6.28) * 0.4
          }
          // Canopies sway.
          if (type.sway) {
            rot = p.rot + Math.sin(this.time * 1.1 + p.phase * 6.28) * type.sway
          }
          if (type.spin) {
            rot = p.rot + this.time * type.spin.speed
          }

          // Ground plane sits at z = -2.4 (HomeFieldEnvironment). Props must
          // stand ON it, not behind it — the old placeholder barns used -1.7.
          let scale = p.scale
          let sink = 0
          let list = 0
          if (p.wreckTime >= 0) {
            const t = Math.min(1, p.wreckTime / 1.1)
            // Ease-out collapse: fast list, slow settle.
            scale = p.scale * (1 - t * 0.55)
            sink = -t * 1.4
            list = t * 0.9
            if (t >= 1) scale = 0 // fully collapsed; zero-scale culls it cheaply
          }

          // Ground plane sits at z = -2.4 (HomeFieldEnvironment). Props must
          // stand ON it, not behind it — the old placeholder barns used -1.7.
          //
          // A lifted prop rides on top of another (the windmill fan on its
          // tower) and turns about the axis it FACES rather than about the
          // vertical, so it spins in its own plane instead of yawing.
          const lift = type.lift
          if (lift) {
            this.dummy.position.set(x, y + lift.y, -2.3 + lift.z + sink)
            this.dummy.rotation.set(list, type.spinFacing ? this.time * type.spinFacing : 0, 0)
          } else {
            this.dummy.position.set(x, y, -2.3 + sink)
            this.dummy.rotation.set(list, 0, rot)
          }
          this.dummy.scale.setScalar(scale)
          this.dummy.updateMatrix()
          batch.mesh.setMatrixAt(i, this.dummy.matrix)
        }
        batch.mesh.instanceMatrix.needsUpdate = true
      }
    }
  }

  reset(): void {
    this.scrolled = 0
    this.time = 0
    for (const t of this.types) {
      const spec = DESTRUCTIBLE[t.id]
      for (const p of t.placements) {
        p.hp = spec?.hp ?? Infinity
        p.wreckTime = -1
      }
    }
    this.sync()
  }

  dispose(): void {
    for (const t of this.types) {
      for (const b of t.batches) b.mesh.geometry.dispose()
    }
  }
}

/** Minimal geometry merge — avoids pulling in BufferGeometryUtils. */
function mergeGeometries(list: THREE.BufferGeometry[]): THREE.BufferGeometry | null {
  if (list.length === 0) return null
  const positions: number[] = []
  const normals: number[] = []
  const uvs: number[] = []
  const indices: number[] = []
  let offset = 0

  for (const g of list) {
    const pos = g.getAttribute('position')
    const nor = g.getAttribute('normal')
    const uv = g.getAttribute('uv')
    if (!pos) continue
    for (let i = 0; i < pos.count; i++) {
      positions.push(pos.getX(i), pos.getY(i), pos.getZ(i))
      normals.push(nor ? nor.getX(i) : 0, nor ? nor.getY(i) : 1, nor ? nor.getZ(i) : 0)
      uvs.push(uv ? uv.getX(i) : 0, uv ? uv.getY(i) : 0)
    }
    const idx = g.getIndex()
    if (idx) {
      for (let i = 0; i < idx.count; i++) indices.push(idx.getX(i) + offset)
    } else {
      for (let i = 0; i < pos.count; i++) indices.push(i + offset)
    }
    offset += pos.count
    g.dispose()
  }

  const out = new THREE.BufferGeometry()
  out.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  out.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3))
  out.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  out.setIndex(indices)
  return out
}
